/**
 * Phase 6E.1 — Public Selective Entry Resources page.
 *
 * Polished student-facing page (not admin table) that consumes the EXISTING
 * Learning Centre endpoints — does not regenerate or modify any content.
 *
 * - Anonymous visitors: see overview + sample/free items + CTA to enrol.
 * - Logged-in Selective Entry students: prominent "Open Learning Centre" button.
 * - Other logged-in users: see CTA to book a Selective tutor.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Calculator, BookOpenText, Trophy, FileText, ArrowRight, Lock,
  GraduationCap, Sparkles, Brain, Star, Download, PlayCircle, Filter,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import DailyChallengeWidget from '@/components/shared/DailyChallengeWidget';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const TABS = [
  { id: 'maths', label: 'Mathematics', icon: Calculator },
  { id: 'english', label: 'English', icon: BookOpenText },
  { id: 'mocks', label: 'Mock Exams', icon: Trophy },
  { id: 'downloads', label: 'Downloads', icon: FileText },
];

const DIFFICULTY_TONE = {
  Easy: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  Medium: 'bg-amber-50 text-amber-800 border-amber-200',
  Hard: 'bg-rose-50 text-rose-800 border-rose-200',
};

const RESOURCE_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'formula-sheets', label: 'Formulas' },
  { id: 'grammar-guides', label: 'Grammar' },
  { id: 'vocabulary-lists', label: 'Vocabulary' },
  { id: 'revision-notes', label: 'Revision' },
  { id: 'worked-examples', label: 'Worked Examples' },
  { id: 'challenge', label: 'Challenge' },
  { id: 'homework', label: 'Homework' },
];

const TopicCard = ({ t, kind, free, me }) => {
  const tone = DIFFICULTY_TONE[t.difficulty] || 'bg-gray-50 text-gray-700 border-gray-200';
  return (
    <Link to={me ? `/student/learning?topic=${t.slug}` : `/student/login?next=/student/learning?topic=${t.slug}`}
      className="group block bg-white border border-gray-200 rounded-2xl p-5 hover:border-red-600 hover:-translate-y-0.5 transition-all"
      data-testid={`sel-topic-${kind}-${t.slug}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="w-10 h-10 rounded-xl bg-red-50 text-red-700 flex items-center justify-center">
          {kind === 'maths' ? <Calculator className="h-5 w-5" /> : <BookOpenText className="h-5 w-5" />}
        </div>
        {!free && !me && <Lock className="h-3.5 w-3.5 text-gray-400" />}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1 flex items-center gap-2">
        <span className={`inline-block px-1.5 py-0.5 rounded border text-[9px] font-bold ${tone}`}>{t.difficulty || 'Mixed'}</span>
        <span>{t.estimated_minutes || 25} min</span>
      </div>
      <h3 className="font-bold text-base mb-1 group-hover:text-red-700 transition-colors">{t.title}</h3>
      <p className="text-xs text-gray-600 line-clamp-2">{t.summary || (kind === 'maths' ? 'Worked examples · practice questions · mini-quiz' : 'Reading · grammar · vocabulary · cloze drills')}</p>
      <div className="mt-3 text-[10px] uppercase tracking-wider text-red-700 font-bold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">Open <ArrowRight className="h-3 w-3" /></div>
    </Link>
  );
};

const MockCard = ({ m, me }) => {
  const isMaths = (m.slug || '').includes('maths');
  const isEnglish = (m.slug || '').includes('english');
  const isCombined = (m.slug || '').includes('combined');
  const Icon = isCombined ? Trophy : isMaths ? Calculator : isEnglish ? BookOpenText : Trophy;
  return (
    <Link to={me ? `/student/exams?mock=${m.slug}` : `/student/login?next=/student/exams`}
      className="group block bg-white border border-gray-200 rounded-2xl p-5 hover:border-red-600 hover:-translate-y-0.5 transition-all"
      data-testid={`sel-mock-${m.slug}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-11 h-11 rounded-xl ${isCombined ? 'bg-black text-[#D3D86F]' : 'bg-red-50 text-red-700'} flex items-center justify-center shrink-0`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-400">{isCombined ? 'Combined' : isMaths ? 'Mathematics' : 'English'}</div>
          <h3 className="font-bold text-sm leading-tight">{m.title}</h3>
        </div>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <PlayCircle className="h-3 w-3" />
        {m.duration_minutes ? `${m.duration_minutes} min` : 'Timed'}
        {m.questions_count != null && <span>· {m.questions_count} questions</span>}
      </div>
      <Button size="sm" className="mt-4 rounded-full bg-black hover:bg-gray-900 text-[#D3D86F] text-[11px] font-bold uppercase tracking-wider h-8 px-4">
        {me ? 'Start mock' : 'Preview & start'}
      </Button>
    </Link>
  );
};

const ResourceRow = ({ r, me }) => (
  <Link to={me ? `/student/learning?resource=${r.slug}` : `/student/login?next=/student/learning`}
    className="group flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 transition rounded-xl"
    data-testid={`sel-resource-${r.slug}`}>
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-lg bg-red-50 text-red-700 flex items-center justify-center shrink-0">
        <FileText className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="font-bold text-sm truncate">{r.title}</div>
        <div className="text-[10px] uppercase tracking-wider text-gray-400">{(r.category || '').replace('-', ' ')}</div>
      </div>
    </div>
    <div className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-700 font-bold">
      {me ? <><Download className="h-3 w-3" /> Get</> : <><Lock className="h-3 w-3" /> Login</>}
    </div>
  </Link>
);

const SelectiveEntryResourcesPage = () => {
  const [mobile, setMobile] = useState(false);
  const [me, setMe] = useState(null);
  const [planProgram, setPlanProgram] = useState(null);
  const [enrolledPrograms, setEnrolledPrograms] = useState([]);
  const [tab, setTab] = useState('maths');
  const [search, setSearch] = useState('');
  const [resCategory, setResCategory] = useState('all');
  const [mathsTopics, setMathsTopics] = useState([]);
  const [englishTopics, setEnglishTopics] = useState([]);
  const [mocks, setMocks] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [meR, mathsR, engR, mocksR, resR] = await Promise.all([
          ax.get('/api/auth/me').catch(() => ({ data: null })),
          ax.get('/api/learning/tracks/mathematics/topics').catch(() => ({ data: { items: [] } })),
          ax.get('/api/learning/tracks/english/topics').catch(() => ({ data: { items: [] } })),
          ax.get('/api/learning/mocks').catch(() => ({ data: { items: [] } })),
          ax.get('/api/learning/resources').catch(() => ({ data: { items: [] } })),
        ]);
        if (!live) return;
        setMe(meR.data || null);
        setMathsTopics(mathsR.data?.items || []);
        setEnglishTopics(engR.data?.items || []);
        setMocks((mocksR.data?.items || []).filter(m => (m.program_slug || '').includes('selective')));
        setResources((resR.data?.items || []).filter(r => (r.program_slug || '').includes('selective')));
        // Best-effort fetch of logged-in user's plan program (for Open Learning Centre CTA gate)
        if (meR.data?.email) {
          try {
            const [lp, pr] = await Promise.all([
              ax.get('/api/me/student/learning-plan').catch(() => ({ data: null })),
              ax.get('/api/me/student/programs').catch(() => ({ data: null })),
            ]);
            if (!live) return;
            setPlanProgram(lp.data?.plan?.program || null);
            setEnrolledPrograms(pr.data?.items || []);
          } catch (_e) { /* tutor/admin or no plan */ }
        }
      } catch (_e) { /* silent */ }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, []);

  const isSelectiveStudent = useMemo(() => {
    const pp = String(planProgram || '').toLowerCase();
    if (pp.includes('selective') || pp.includes('scholarship')) return true;
    return enrolledPrograms.some(p => {
      const name = String(p.name || '').toLowerCase();
      const slug = String(p.slug || '').toLowerCase();
      return name.includes('selective') || name.includes('scholarship') || slug.includes('selective');
    });
  }, [planProgram, enrolledPrograms]);

  const filteredMaths = useMemo(() =>
    mathsTopics.filter(t => !search || (t.title || '').toLowerCase().includes(search.toLowerCase())),
    [mathsTopics, search]);
  const filteredEnglish = useMemo(() =>
    englishTopics.filter(t => !search || (t.title || '').toLowerCase().includes(search.toLowerCase())),
    [englishTopics, search]);
  const filteredMocks = useMemo(() => {
    let arr = mocks;
    if (search) arr = arr.filter(m => (m.title || '').toLowerCase().includes(search.toLowerCase()));
    return arr;
  }, [mocks, search]);
  const filteredResources = useMemo(() => {
    let arr = resources;
    if (resCategory !== 'all') arr = arr.filter(r => r.category === resCategory);
    if (search) arr = arr.filter(r => (r.title || '').toLowerCase().includes(search.toLowerCase()));
    return arr;
  }, [resources, resCategory, search]);

  // (Card/Row components are hoisted to module scope; we pass `me` as a prop.)

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="selective-entry-resources">
      <Seo title="Selective Entry Resources · Winhouse Tutors"
        canonical="/resources/selective-entry"
        description="Curated Selective Entry, VCE Selective and Scholarship preparation resources — Maths topics, English topics, mock exams, formula sheets and revision notes." />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <FloatingActionButtons />

      {/* HERO */}
      <section className="relative bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-90" style={{
          background: 'radial-gradient(60% 50% at 20% 0%, rgba(220,38,38,0.32) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 40% at 85% 100%, rgba(211,216,111,0.16) 0%, rgba(211,216,111,0) 70%)',
        }} />
        <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-16 sm:pt-32 sm:pb-20">
          <Link to="/resources" className="text-white/60 hover:text-white text-xs inline-flex items-center gap-1 mb-4" data-testid="sel-back-to-resources">
            <ArrowRight className="h-3 w-3 rotate-180" /> Back to Resources
          </Link>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur rounded-full px-3 py-1 text-xs uppercase tracking-[0.18em] mb-5">
            <Sparkles className="h-3.5 w-3.5 text-[#D3D86F]" /> Selective Entry Resource Hub
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight">
            Everything you need for Selective Entry
            <span className="block text-[#D3D86F]">in one place.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/75 max-w-2xl mt-5 leading-relaxed">
            {mathsTopics.length} Maths topics · {englishTopics.length} English topics · {mocks.length} mock exams · {resources.length} downloadable resources — all built around the Victorian Selective Entry, VCE Selective Stream and Scholarship exam formats.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            {isSelectiveStudent ? (
              <Link to="/student/learning" data-testid="sel-open-lc">
                <Button className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-bold px-6 h-11 text-sm rounded-full">
                  Continue Learning <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            ) : me ? (
              <Link to="/get-started?topic=selective-entry" data-testid="sel-book-tutor">
                <Button className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-bold px-6 h-11 text-sm rounded-full">
                  Book Selective Tutor <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/student/signup?program=selective-entry" data-testid="sel-signup">
                  <Button className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-bold px-6 h-11 text-sm rounded-full">
                    Create Student Account <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/get-started?topic=selective-entry" data-testid="sel-book-anon">
                  <Button variant="outline" className="bg-white/5 backdrop-blur border-white/25 text-white hover:bg-white/15 hover:text-white px-6 h-11 text-sm rounded-full">
                    Book Free Assessment
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* TABS */}
      <div className="sticky top-16 bg-white/95 backdrop-blur border-b border-gray-100 z-30">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between gap-4 flex-wrap py-3">
          <div className="flex items-center gap-1 overflow-x-auto" data-testid="sel-tabs">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sel-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-xs font-bold uppercase tracking-wider transition ${tab === t.id ? 'bg-black text-[#D3D86F]' : 'text-gray-600 hover:bg-gray-100'}`}>
                <t.icon className="h-3 w-3" /> {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 w-full md:w-72">
            <Filter className="h-3.5 w-3.5 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search topics, mocks, resources…"
              className="w-full text-sm outline-none border-b border-transparent focus:border-black px-1 py-1.5"
              data-testid="sel-search" />
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto px-6 py-10 w-full">
        {/* Phase 6E.2 — Daily Selective Challenge */}
        <div className="mb-8">
          <DailyChallengeWidget />
        </div>

        {loading && <p className="text-sm text-gray-400 italic">Loading Selective Entry library…</p>}

        {/* MATHS TAB */}
        {!loading && tab === 'maths' && (
          <section data-testid="sel-tab-content-maths">
            <SectionHeader title="Mathematics practice" count={filteredMaths.length} desc="Topic-tagged lessons with worked examples, practice questions and a mini-quiz." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMaths.map((t, i) => <TopicCard key={t.slug} t={t} kind="maths" free={i < 2} me={me} />)}
            </div>
            {filteredMaths.length === 0 && <p className="text-sm text-gray-400 italic">No topics match your search.</p>}
          </section>
        )}

        {/* ENGLISH TAB */}
        {!loading && tab === 'english' && (
          <section data-testid="sel-tab-content-english">
            <SectionHeader title="English practice" count={filteredEnglish.length} desc="Reading, comprehension, vocabulary, grammar, cloze and writing — each with structured drills." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEnglish.map((t, i) => <TopicCard key={t.slug} t={t} kind="english" free={i < 2} me={me} />)}
            </div>
            {filteredEnglish.length === 0 && <p className="text-sm text-gray-400 italic">No topics match your search.</p>}
          </section>
        )}

        {/* MOCKS TAB */}
        {!loading && tab === 'mocks' && (
          <section data-testid="sel-tab-content-mocks">
            <SectionHeader title="Mock exams" count={filteredMocks.length} desc="Maths, English and combined Selective papers — real-time timer, instant review." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMocks.map(m => <MockCard key={m.slug} m={m} me={me} />)}
            </div>
            {filteredMocks.length === 0 && <p className="text-sm text-gray-400 italic">No mocks match your search.</p>}
          </section>
        )}

        {/* DOWNLOADS TAB */}
        {!loading && tab === 'downloads' && (
          <section data-testid="sel-tab-content-downloads">
            <SectionHeader title="Downloadable resources" count={filteredResources.length} desc="Formula sheets, grammar guides, vocab decks, revision notes, challenge sets and homework sheets." />
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="sel-res-categories">
              {RESOURCE_CATEGORIES.map(c => (
                <button key={c.id} onClick={() => setResCategory(c.id)}
                  className={`px-3 h-8 rounded-full text-[11px] font-bold uppercase tracking-wider transition ${resCategory === c.id ? 'bg-black text-[#D3D86F]' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                  data-testid={`sel-res-cat-${c.id}`}>{c.label}</button>
              ))}
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl divide-y divide-gray-100">
              {filteredResources.length === 0 && <p className="text-sm text-gray-400 italic p-6">No resources match this category/search.</p>}
              {filteredResources.map(r => <ResourceRow key={r.slug} r={r} me={me} />)}
            </div>
          </section>
        )}

        {/* CTA STRIP */}
        <section className="mt-16 grid md:grid-cols-3 gap-4">
          <CtaTile icon={GraduationCap} title="Create student account" desc="Track progress, unlock all topics, and join the Selective Entry leaderboard." to={me ? '/student/dashboard' : '/student/signup?program=selective-entry'} testid="sel-cta-create" />
          <CtaTile icon={Brain} title="Book Selective tutor" desc="1-on-1 expert sessions or 4-session intensive packages — Maths and English specialists." to="/get-started?topic=selective-entry" testid="sel-cta-book" />
          <CtaTile icon={Star} title="Open Learning Centre" desc={isSelectiveStudent ? 'Continue your learning streak and tackle today\u2019s recommended revision.' : 'Available to enrolled Selective Entry students after sign-in.'} to={isSelectiveStudent ? '/student/learning' : '/student/login?next=/student/learning'} testid="sel-cta-learning" disabled={!isSelectiveStudent && !!me} />
        </section>
      </main>

      <Footer />
    </div>
  );
};

const SectionHeader = ({ title, count, desc }) => (
  <div className="mb-6">
    <div className="flex items-end justify-between flex-wrap gap-2">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2>
        <p className="text-sm text-gray-600 mt-1 max-w-2xl">{desc}</p>
      </div>
      <span className="text-[10px] uppercase tracking-[2px] text-gray-400 font-bold">{count} {count === 1 ? 'item' : 'items'}</span>
    </div>
  </div>
);

const CtaTile = ({ icon: Icon, title, desc, to, testid, disabled = false }) => {
  const className = `block bg-[#FAF9F6] border rounded-2xl p-5 transition ${disabled ? 'opacity-60 pointer-events-none border-gray-200' : 'border-gray-200 hover:border-red-600 hover:-translate-y-0.5'}`;
  return (
    <Link to={to} className={className} data-testid={testid}>
      <div className="w-10 h-10 rounded-xl bg-red-50 text-red-700 flex items-center justify-center mb-3">
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-bold text-base mb-1">{title}</div>
      <p className="text-xs text-gray-600">{desc}</p>
      <div className="mt-3 text-[10px] uppercase tracking-wider text-red-700 font-bold inline-flex items-center gap-1">Open <ArrowRight className="h-3 w-3" /></div>
    </Link>
  );
};

export default SelectiveEntryResourcesPage;
